import type { PremiumGroup, Holiday, PremiumAchPull, PremiumAchPullStatus } from '../types'

import { useMutation } from '@tanstack/react-query'
import { Button, Dropdown, Input } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import $api from '../../../API'
import { formatDate, pluralize } from '../formatters'

import { getNextPullDate } from '.'

export interface PremiumGroupFooterProps {
  pulls: PremiumAchPull[]
  selected: PremiumGroup[]
  holidays: Holiday[]
  onClearSelection: () => void
}

export const PremiumGroupsFooter = (props: PremiumGroupFooterProps): JSX.Element => {
  const navigate = useNavigate()
  const [batchName, setBatchName] = useState('')
  const [validBatch, setValidBatch] = useState(false)
  const [pullsToAdd, setPullsToAdd] = useState<PremiumAchPull[]>([])

  const isValidBatch = useCallback((groups: PremiumGroup[]): boolean => {
    if (!groups[0]) {
      return false
    }
    const group = groups[0]
    return (
      !groups.some(
        g =>
          g.location.id !== group.location.id &&
          g.premium_schedule_day !== group.premium_schedule_day
      ) && !groups.some(g => !g.flags.includes('has-open-invoices'))
    )
  }, [])

  // todo - implement in/out interfaces
  const createMutation = useMutation<{ id: string }, Error, unknown>({
    mutationFn: async data => {
      try {
        const response = await $api.post<{ id: string }>('/batch', data, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        return response.data
      } catch {
        throw new Error('Failed to create batch')
      }
    },
    onSuccess: data => {
      navigate(`/premium/pull/${data.id}`)
    },
    onError: error => {
      // todo - show error message
      console.error('Failed to create batch:', error)
    }
  })

  // todo - implement in/out interfaces
  const insertMutation = useMutation<{ id: string }, Error, unknown>({
    mutationFn: async data => {
      try {
        const response = await $api.patch<{ id: string }>('/batch/invoice', data)
        return response.data
      } catch {
        throw new Error('Failed to create batch')
      }
    },
    onSuccess: data => {
      navigate(`/premium/pull/${data.id}`)
    },
    onError: error => {
      // todo - show error message
      console.error('Failed to create batch:', error)
    }
  })

  const handleBatchNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setBatchName(e.target.value)
  }

  const handleCreatePull = useCallback(() => {
    const groups = props.selected
    if (groups[0] !== undefined) {
      const sendingDate = getNextPullDate({
        scheduleDay: groups[0].premium_schedule_day ?? 0,
        holidays: props.holidays,
        startDate: new Date()
      })
      createMutation.mutate({
        name: batchName,
        sendingDate,
        locationId: groups[0].location.id,
        groupIds: groups.map(g => g.id)
      })
    }
  }, [batchName, props.selected, props.holidays, createMutation])

  const handleAddToPull = useCallback(
    (item: { key: string }) => {
      const groupIds = props.selected.map(g => g.id)
      const id = item.key
      insertMutation.mutate({ id, groupIds })
    },
    [props.selected, insertMutation]
  )

  useEffect(() => {
    const allowedPullStatuses: PremiumAchPullStatus[] = ['initial_review']
    const isValid = isValidBatch(props.selected)
    setValidBatch(isValid)
    if (isValid && props.selected[0]) {
      const group = props.selected[0]
      const pulls = props.pulls.filter(pull => {
        return allowedPullStatuses.includes(pull.status) && group.location.id === pull.location.id
      })
      setPullsToAdd(pulls)
    } else {
      setPullsToAdd([])
    }
  }, [props.selected, isValidBatch, props.pulls])

  useEffect(() => {
    if (props.selected[0]) {
      if (validBatch) {
        const group = props.selected[0]
        let date = ''
        try {
          date = formatDate(
            getNextPullDate({
              scheduleDay: group.premium_schedule_day ?? 0,
              holidays: props.holidays,
              startDate: new Date()
            }).toISOString()
          )
        } catch (e) {
          console.info(e)
        }
        setBatchName(`${group.location.short_name} ${date}`)
      } else {
        setBatchName('')
      }
    } else {
      setBatchName('')
    }
    setValidBatch(isValidBatch(props.selected))
  }, [props.selected, validBatch, isValidBatch, props.holidays])

  return (
    <div className='flex flex-row' style={{ alignItems: 'center' }}>
      <span style={{ fontWeight: 'bold' }}>
        {props.selected.length === 0 ? 'No' : props.selected.length}
        {pluralize(props.selected.length, ' group ', ' groups ')}
        selected
      </span>
      {props.selected.length > 0 ? (
        <a
          href='#'
          className='text-blue-700 text-[14px]'
          style={{ marginLeft: '10px' }}
          onClick={props.onClearSelection}
        >
          Clear selection
        </a>
      ) : null}
      <div className='ml-auto flex flex-row'>
        <Input.Group style={{ width: 300 }} compact>
          <Input
            className='ml-auto'
            style={{ width: '60%' }}
            placeholder='Enter batch name'
            onChange={handleBatchNameChange}
            value={batchName}
          />
          <Button
            type='primary'
            loading={createMutation.isLoading}
            disabled={!validBatch || insertMutation.isLoading}
            onClick={handleCreatePull}
            style={{
              width: '40%',
              borderRadius: '0 8px 8px 0'
            }}
          >
            Create Pull
          </Button>
        </Input.Group>
        {validBatch && pullsToAdd.length > 0 && (
          <Dropdown
            className='ml-2'
            menu={{
              items: pullsToAdd.map(p => ({ key: p.id, label: p.name })),
              onClick: handleAddToPull
            }}
          >
            <Button
              type='primary'
              style={{ width: 'auto' }}
              className='ml-2'
              loading={insertMutation.isLoading}
              disabled={createMutation.isLoading}
            >
              Add to Pull
            </Button>
          </Dropdown>
        )}
      </div>
    </div>
  )
}
